import { addToast, Button, Modal, TextOutlinedInput } from '@octano/global-ui';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import { moneyFormatter } from '../../../../../utils/currency';
import { generateMultiPaymentLink, generatePaymentLink } from '../api';
import { Debt } from '../types';

interface Props {
  studentId: number;
}

export interface PaymentLinkModalRef {
  openModal: (debts: Debt[]) => void;
  closeModal: () => void;
}

const dictPrefix = 'financialPortal.currentAccount.tabs.currentAccount.paymentLink';

interface FormFields {
  url: string;
}

// Códigos que significan "una cuota que estaba seleccionada dejó de ser
// pagable entre que se renderizó la tabla y se apretó el botón" (otro pago la
// canceló, se resolvió por otra vía, etc.) — el backend los manda con o sin
// `:<debtId>` según el endpoint (uno o varias deudas). Es el fallo más
// probable en la práctica, y el único accionable con "refresca y vuelve a
// seleccionar"; todo lo demás (flags apagados, proveedores mezclados...) se
// queda con el mensaje genérico.
const STALE_SELECTION_ERROR_CODES = new Set([
  'NO_PAYABLE_EXTERNAL_DEBT',
  'DEBT_ALREADY_PAID',
]);

const PaymentLinkModal = forwardRef<PaymentLinkModalRef, Props>(
  ({ studentId }, ref) => {
    const { t } = useTranslation();
    const [modal, setModal] = useState(false);
    const [loading, setLoading] = useState(false);
    // Código de error del backend (`MULTI_PAYMENT_LINK_DISABLED`,
    // `NO_PAYABLE_EXTERNAL_DEBT:<id>`, ...), no un booleano: distintos códigos
    // requieren distintos mensajes (ver STALE_SELECTION_ERROR_CODES arriba).
    // `null` = sin error.
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
    const [debts, setDebts] = useState<Debt[]>([]);
    const { control, setValue, watch } = useForm<FormFields>({
      defaultValues: { url: '' },
    });
    const url = watch('url');

    const load = async (selected: Debt[]) => {
      if (!selected.length) {
        return;
      }
      setLoading(true);
      setErrorCode(null);
      setQrCodeDataUrl(null);
      setValue('url', '');

      // Una sola cuota sigue por el endpoint de siempre: es el único camino
      // que tienen las pasarelas que no saben cobrar varias juntas.
      const { data, error: reqError } =
        selected.length > 1
          ? await generateMultiPaymentLink(
              studentId,
              selected.map((debt) => debt.id),
            )
          : await generatePaymentLink(studentId, selected[0].id);

      setLoading(false);
      if (reqError || !data) {
        // El body de error del backend viaja en `reqError.data` (ver
        // src/api/request.ts, AxiosResultErrorHttp) con forma
        // `{ message: string }` — el código puede traer un id pegado con ":"
        // (p.ej. `NO_PAYABLE_EXTERNAL_DEBT:cc-debt-2`) cuando viene del
        // endpoint de varias deudas, que nombra la fila que rompió el lote.
        const message = (reqError as { data?: { message?: unknown } } | undefined)
          ?.data?.message;
        setErrorCode(typeof message === 'string' ? message : 'UNKNOWN');
        return;
      }
      setValue('url', data.url);
      setQrCodeDataUrl(data.qrCodeDataUrl);
    };

    // Sólo el código, sin el `:<debtId>` que algunos endpoints le pegan atrás.
    const errorCodePrefix = errorCode?.split(':')[0] ?? null;
    const isStaleSelectionError =
      errorCodePrefix !== null &&
      STALE_SELECTION_ERROR_CODES.has(errorCodePrefix);

    useImperativeHandle(ref, () => ({
      openModal(selected: Debt[]) {
        setDebts(selected);
        setModal(true);
        void load(selected);
      },
      closeModal() {
        setModal(false);
      },
    }));

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(url);
        addToast({ text: t(`${dictPrefix}.copied`), color: 'success' });
      } catch {
        addToast({ text: t(`${dictPrefix}.copyError`), color: 'danger' });
      }
    };

    return (
      <Modal isOpen={modal} toggle={() => setModal(!modal)} size="md">
        <Container fluid className="text-center">
          <p className="text-dark fs-22 mb-3">{t(`${dictPrefix}.title`)}</p>
          <p className="fs-18">{t(`${dictPrefix}.description`)}</p>

          {loading && <p>...</p>}

          {!loading && errorCode && (
            <p className="text-danger">
              {t(
                `${dictPrefix}.${
                  isStaleSelectionError ? 'staleSelectionError' : 'generateError'
                }`,
              )}
            </p>
          )}

          {!loading && !errorCode && qrCodeDataUrl && (
            <>
              {debts.length > 1 && (
                <>
                  <p className="fs-14 fw-600 mb-1">
                    {t(`${dictPrefix}.includedTitle`)}
                  </p>
                  <ul className="list-unstyled fs-14 text-muted mb-3">
                    {debts.map((debt) => (
                      <li key={debt.id}>
                        {t(`${dictPrefix}.includedDebt`, {
                          concept: debt.concept,
                          installment: debt.numberOfInstallment,
                          amount: moneyFormatter().format(
                            Math.round(Number(debt.outstandingDebt ?? 0)),
                          ),
                        })}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <Row className="justify-content-center my-3">
                <img
                  src={qrCodeDataUrl}
                  alt={t(`${dictPrefix}.qrAlt`)}
                  width={180}
                  height={180}
                />
              </Row>
              <Row>
                <Col xs={12}>
                  <TextOutlinedInput
                    control={control}
                    label=""
                    name="url"
                    readOnly
                  />
                </Col>
              </Row>
              <div className="d-flex justify-content-around mt-4" style={{ gap: 10 }}>
                <div className="flex-fill">
                  <Button
                    className="w-100"
                    outlined
                    text={t(`${dictPrefix}.close`)}
                    onClick={() => setModal(false)}
                  />
                </div>
                <div className="flex-fill">
                  <Button
                    className="w-100"
                    text={t(`${dictPrefix}.copy`)}
                    onClick={handleCopy}
                  />
                </div>
              </div>
            </>
          )}
        </Container>
      </Modal>
    );
  },
);

export default PaymentLinkModal;
